import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, X } from "lucide-react";

/**
 * PushToProdButton — a compact button + modal that pushes a preview record
 * (product, promo, article, coa) to production via the /push-to-production
 * endpoint family. The remote admin credentials are gathered once per push
 * and never stored (session storage only remembers URL + email).
 *
 * Props:
 *   endpoint  — full API path relative to `/api`, e.g. `/admin/promo-codes/${id}/push-to-production`
 *   label     — display label of the record (SKU / code / slug / batch)
 *   entity    — "product" | "promo" | "article" | "coa" — controls copy
 *   testid    — data-testid for the trigger button
 *   showOverride — if true, exposes the X-Compliance-Override checkbox (default true)
 */
export function PushToProdButton({ endpoint, label, entity = "record", testid, showOverride = true }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    prod_api_url: sessionStorage.getItem("axn_prod_api_url") || "https://nebulaaminos.com",
    prod_email: sessionStorage.getItem("axn_prod_email") || "support@nebulaaminos.com",
    prod_password: "",
    override_compliance: true,
  });

  const close = () => {
    setOpen(false);
    setForm((f) => ({ ...f, prod_password: "" }));
  };

  const run = async () => {
    if (!form.prod_password) {
      toast.error("Enter your production admin password.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, {
        prod_api_url: form.prod_api_url,
        prod_email: form.prod_email,
        prod_password: form.prod_password,
        override_compliance: form.override_compliance,
      });
      sessionStorage.setItem("axn_prod_api_url", form.prod_api_url);
      sessionStorage.setItem("axn_prod_email", form.prod_email);
      toast.success(`Pushed ${label} — ${data.action} on production`);
      close();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Push failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        disabled={busy}
        className="inline-flex items-center gap-1 px-2 py-1 border border-[#D1D5DB] rounded text-[11px] font-mono uppercase tracking-wider text-[#0B6623] hover:border-[#0B6623] disabled:opacity-50"
        title={`Push this ${entity} to production`}
        data-testid={testid}
      >
        <UploadCloud className="h-3 w-3" /> {busy ? "Pushing…" : "Push → Prod"}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={close} data-testid="push-prod-modal">
          <div className="bg-white border border-[#D1D5DB] rounded w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-[#D1D5DB] flex items-center justify-between">
              <div>
                <div className="label-eyebrow">Push {entity} to production</div>
                <div className="font-bold tracking-tight mt-0.5 font-mono text-sm">{label}</div>
              </div>
              <button onClick={close} aria-label="Close" data-testid="push-prod-close" className="p-1 hover:bg-[#F7F7F8]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="label-eyebrow block mb-1">Production API URL</label>
                <input
                  value={form.prod_api_url}
                  onChange={(e) => setForm({ ...form, prod_api_url: e.target.value.trim() })}
                  className="w-full px-3 py-2 border border-[#D1D5DB] font-mono text-sm rounded"
                  data-testid="push-prod-url"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Prod admin email</label>
                <input
                  type="email"
                  value={form.prod_email}
                  onChange={(e) => setForm({ ...form, prod_email: e.target.value.trim() })}
                  className="w-full px-3 py-2 border border-[#D1D5DB] text-sm rounded"
                  data-testid="push-prod-email"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Prod admin password</label>
                <input
                  type="password"
                  value={form.prod_password}
                  onChange={(e) => setForm({ ...form, prod_password: e.target.value })}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-[#D1D5DB] text-sm rounded"
                  data-testid="push-prod-password"
                />
                <p className="text-[10.5px] font-mono uppercase tracking-wider text-[#5E636E] mt-1">Used once, never stored on the server.</p>
              </div>
              {showOverride && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.override_compliance}
                    onChange={(e) => setForm({ ...form, override_compliance: e.target.checked })}
                    className="h-4 w-4 accent-[#003366]"
                    data-testid="push-prod-override"
                  />
                  <span>Send X-Compliance-Override (recommended)</span>
                </label>
              )}
            </div>
            <div className="px-5 py-3 border-t border-[#D1D5DB] flex justify-end gap-3">
              <button onClick={close} className="btn-secondary" data-testid="push-prod-cancel">Cancel</button>
              <button
                onClick={run}
                disabled={busy}
                className="btn-primary inline-flex items-center gap-2 disabled:bg-[#5E636E]"
                style={{ background: "#0B6623" }}
                data-testid="push-prod-run"
              >
                <UploadCloud className="h-4 w-4" /> {busy ? "Pushing…" : "Push to production"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
